/* assets/promo22.js — the 2+2 promo's badge layer and measurement, one code path
 * for every surface.
 *
 * WHY CLIENT-SIDE for the card badges: the product cards live as EIGHT separate
 * .dcard copies plus snippets/product-item.liquid (documented in CLAUDE.md), and
 * two of the copies are ?view= AJAX endpoints this repo's push rules keep off
 * the allowlist. Editing nine render sites for one promo badge - and unediting
 * them after - is exactly the maintenance trap the codebase already suffers
 * from. Instead: fetch the promo collection's handles once per session, decorate
 * every matching card link wherever it appears (collection grid, search, home
 * rails, drawer cross-sells, load-more pages), and vanish when the collection
 * is empty. The collection is the only switch.
 *
 * MEASUREMENT: every aspect flows through window.mfTrackPush as mf_promo_step,
 * which assets/mf-track.js splits into promo:<step> beacon steps - so the
 * server-side add/checkout joins (step_add/step_chk) price every promo
 * touchpoint with zero server changes:
 *   promo:bar_view / promo:bar_click        - announcement bar mentioning 2+2
 *   promo:cards_seen / promo:card_click     - badge layer
 *   promo:pdp_view                          - the PDP promo line rendered
 *   promo:drawer_none|one_more|add_gifts|one_gift|applied - drawer states
 *   promo:cases_1..cases_4                  - cart-depth high-water (cumulative)
 */
(function () {
  'use strict';

  /* 4x2: the OFF arm must neither see nor measure the promo. The deal script
     in the head sets __mfP22 before any defer script runs; adoption to 'on'
     mid-page self-heals on the next navigation. */
  /* 1+1 era (2026-08-30): the layer serves the mf_p11 ON arm. The retired
     2+2 gate stays off for everyone; steps get a p11_ prefix so the frozen
     promo:* series keep their 2+2-era meaning untouched. */
  if (window.__mfP11 !== 'on') { return; }
  var D = document, W = window;

  /* The drawer bar (mini-cart static section, rendered site-wide) is the switch
     signal: no [data-promo22-state] element means the merchant turned the promo
     off in the theme editor - nothing fetches, nothing decorates. The collection
     handle rides the same element so all surfaces share one source. */
  function promoEl() { return D.querySelector('[data-promo22-state]'); }

  function track(step, extra) {
    try {
      if (W.mfTrackPush) {
        var o = { mf_step: 'p11_' + step };
        if (extra) { for (var k in extra) { o[k] = extra[k]; } }
        W.mfTrackPush('mf_promo_step', o);
      }
    } catch (e) {}
  }
  var onceSent = {};
  function trackOnce(step, extra) {
    if (onceSent[step]) { return; }
    onceSent[step] = 1;
    track(step, extra);
  }

  /* What counts as "the promo bar's text": the merchant writes the announcement
     copy freely, so match every framing that has actually shipped - "2+2",
     "BUY2GET2", "vasarolj 2 / ajandekba 2". Diacritic-tolerant (a vs á, o vs ó). */
  var BAR_RE = /1\s*\+\s*1|2\s*\+\s*2|buy\s*[12]\s*get|aj[áa]nd[ée]kba|\d\s*tokot/i;

  /* Cart depth, session-level high-water mark: cases_1..cases_4 are CUMULATIVE
     levels (cases_4 = reached 4 or more), reported once per session, so the
     dashboard reads "sessions that reached depth N" and exact-N = level N minus
     level N+1. The count itself is server-rendered on the drawer bar. */
  var casesMem = 0; /* mirror for storage-blocked contexts, so the ladder cannot
                       re-fire more than once per pageview there */
  function reportCases() {
    /* mfTrackPush missing means track() would silently no-op - advancing the
       stored high-water in that state loses the level for the whole session */
    if (!W.mfTrackPush) { return; }
    var el = promoEl();
    if (!el) { return; }
    var n = parseInt(el.getAttribute('data-promo22-cases') || '0', 10) || 0;
    if (n > 4) { n = 4; }
    var done = casesMem;
    try {
      var st = parseInt(sessionStorage.getItem('p22c') || '0', 10) || 0;
      if (st > done) { done = st; }
    } catch (e) {}
    for (var lvl = done + 1; lvl <= n; lvl++) { track('cases_' + lvl); }
    if (n > done) {
      casesMem = n;
      try { sessionStorage.setItem('p22c', String(n)); } catch (e) {}
    } else { casesMem = done; }
  }

  /* ── the switch: fetch the promo collection's product handles, cached 30 min.
     A 404 or an empty list means the promo is off - every surface stays dark. ── */
  function getHandles(collection) {
    try {
      var c = JSON.parse(sessionStorage.getItem('p22h') || 'null');
      if (c && c.t && c.k === collection && (Date.now() - c.t) < 1800000) { return Promise.resolve(c.h); }
    } catch (e) {}
    /* all-cases is the whole case catalogue - up to 3 pages of 250 covers it;
       beyond that the tail simply goes unbadged, which is honest degradation */
    function page(n) {
      return fetch('/collections/' + collection + '/products.json?limit=250&page=' + n)
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (j) { return (j.products || []).map(function (p) { return p.handle; }); })
        .catch(function () { return []; });
    }
    return page(1).then(function (h1) {
      if (h1.length < 250) { return h1; }
      return page(2).then(function (h2) {
        var all = h1.concat(h2);
        if (h2.length < 250) { return all; }
        return page(3).then(function (h3) { return all.concat(h3); });
      });
    }).then(function (h) {
      try { sessionStorage.setItem('p22h', JSON.stringify({ t: Date.now(), k: collection, h: h })); } catch (e) {}
      return h;
    });
  }

  var styleDone = false;
  function ensureStyle() {
    if (styleDone) { return; }
    styleDone = true;
    var st = D.createElement('style');
    /* rhode voice: the promo spans the ENTIRE case catalogue, so a black slab on
       every card is a repeated shout that differentiates nothing - a quiet accent
       micro-label in its own row reads as a system, not a sticker */
    st.textContent = '.p22-label{display:block;margin-top:7px;color:var(--accent,#8F00FF);' +
      'font-weight:800;font-size:10px;letter-spacing:.08em;text-transform:uppercase;' +
      'text-align:left;' +
      'font-family:var(--ui,"Gabarito",sans-serif);pointer-events:none;' +
      'white-space:nowrap;overflow:hidden;text-overflow:ellipsis}';
    D.head.appendChild(st);
  }

  function decorate(handles, badgeText) {
    if (!handles.length) { return; }
    var set = {};
    handles.forEach(function (h) { set[h] = 1; });
    ensureStyle();
    /* the big .dcard cards get the label row; the small drawer/design tiles are
       too narrow for a text bar and stay clean */
    var cards = D.querySelectorAll('a.dcard');
    var hit = 0;
    for (var i = 0; i < cards.length; i++) {
      var a = cards[i];
      if (a.getAttribute('data-p22')) { continue; }
      var m = String(a.getAttribute('href') || '').match(/\/products\/([a-z0-9-]+)/);
      if (!m || !set[m[1]]) { continue; }
      a.setAttribute('data-p22', '1');
      var b = D.createElement('span');
      b.className = 'p22-label';
      b.textContent = badgeText;
      a.appendChild(b);
      hit++;
    }
    if (hit) { trackOnce('cards_seen', { mf_count: hit }); }
  }

  function boot() {
    var el = promoEl();
    if (!el) { return; }
    var collection = el.getAttribute('data-promo22-collection') || 'all-cases';
    var badgeText = el.getAttribute('data-promo22-badge') || '2+2';
    /* ── measurement wiring, UNCONDITIONAL: it must not depend on the
       products.json fetch that only the badge layer needs - a 429'd or empty
       collection response must not silence the drawer/bar/depth metrics ── */

    /* second-case picker add (snippets/promo22-bar.liquid one_more state):
       the rhode single-POST pattern - /cart/add.js carries sections so the
       drawer paints from the response with zero extra GETs, then one shared
       /cart.js feeds cart:updated with the full payload (theme.js:6076 and
       custom.js:1324 both need cart.items there - never send them a stub). */
    var pickBusy = false;
    function pickAdd(btn) {
      if (pickBusy) { return; }
      var vid = parseInt(btn.getAttribute('data-variant-id'), 10);
      if (!vid) { return; }
      pickBusy = true;
      btn.setAttribute('aria-busy', 'true');
      track('pick_add', { mf_option_value: btn.getAttribute('data-p11-name') || '' });
      /* the drawer-level twin of the PLP weave question: do the mixed-in
         fresh designs earn their slots? */
      if (btn.getAttribute('data-p11-src') === 'new') { track('pick_add_new'); }
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: vid, quantity: 1 }], sections: 'mini-cart' })
      }).then(function (r) {
        if (!r.ok) { throw new Error('cart/add.js returned ' + r.status); }
        return r.json();
      }).then(function (data) {
        /* invalidate BEFORE dispatching, or the consumers woken by the dispatch
           cache-hit a pre-add cart (the rhode ordering, product-rhode:3510-3516) */
        if (W.mfCart) { W.mfCart.invalidate(); }
        var painted = !!(data && data.sections && data.sections['mini-cart']);
        if (painted) {
          var known = W.mfCart ? W.mfCart.count() : 0;
          D.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { cart: { item_count: (known || 0) + 1, sections: data.sections } }
          }));
        }
        var load = W.mfCart
          ? W.mfCart.get()
          : fetch('/cart.js').then(function (r2) { return r2.json(); });
        return load.then(function (cart) {
          D.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
          if (!painted) {
            D.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
          }
        });
      }).catch(function (e) {
        try { console.error('[mf] p11 pick add failed', e); } catch (e2) {}
        /* re-enable for a retry; on success the re-render replaces the node */
        btn.removeAttribute('aria-busy');
      }).then(function () { pickBusy = false; });
    }

    /* ── second-case quick configurator: ONE control - the device. Type and
       colour sit on the Full-Shock/Black default the server picked; the tile
       carries the design's brand_products map, so another device resolves
       client-side from /products/<handle>.js. The shown price is struck
       through with a CADOU badge - the function frees the pair's cheapest
       the moment the line lands. The panel lives inside the drawer, so a
       cart re-render clears it naturally. ── */
    var cfg = null; /* { name, src, img, vid, price, dev, devices:[{label,handle}], cache:{} } */
    function cfgBox() { return D.querySelector('[data-p11-config]'); }
    function escHtml(x) {
      return String(x == null ? '' : x)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;')
        .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
    function parseDevices(str) {
      var out = [], parts = String(str || '').split(','), i, kv, l, h;
      for (i = 0; i < parts.length; i++) {
        kv = parts[i].split('=>');
        if (kv.length !== 2) { continue; }
        l = kv[0].replace(/^\s+|\s+$/g, '');
        h = kv[1].replace(/^\s+|\s+$/g, '');
        if (l && h) { out.push({ label: l, handle: h }); }
      }
      return out;
    }
    function fmtMoney(cents) {
      var n = Math.round(cents / 100);
      /* HU: the shop format is {{amount_no_decimals_with_comma_separator}} Ft,
         whose amount token is period-grouped. Injecting the bare integer would
         print "10990 Ft", so group first, then substitute. */
      var g = String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      try {
        var f = W.themeVariables && W.themeVariables.settings && W.themeVariables.settings.moneyFormat;
        if (f) { return String(f).replace(/\{\{\s*amount[^}]*\}\}/, g).replace(/<[^>]+>/g, ''); }
      } catch (e) {}
      return g + ' Ft';
    }
    function renderCfg() {
      var box = cfgBox();
      if (!box || !cfg) { return; }
      var i, d, brand, prevBrand;
      var h = '<div class="promo22__cfg-head">' +
        '<img src="' + escHtml(cfg.img) + '" alt="">' +
        '<span class="promo22__cfg-name">' + escHtml(cfg.name) + '</span>' +
        '<span class="promo22__cfg-price">' +
          (cfg.price ? '<s>' + escHtml(cfg.price) + '</s><span class="promo22__cfg-free">AJÁNDÉK</span>' : escHtml(cfg.busy ? '…' : '')) +
        '</span>' +
        '<button type="button" class="promo22__cfg-x" data-p11-cfg-close aria-label="Bezárás">&times;</button>' +
        '</div>';
      if (cfg.devices.length > 1) {
        h += '<label class="promo22__cfg-devlab">Telefon</label>';
        h += '<select class="promo22__cfg-dev" data-p11-dev-sel>';
        prevBrand = '';
        for (i = 0; i < cfg.devices.length; i++) {
          d = cfg.devices[i];
          brand = d.label.split(' ')[0];
          if (brand !== prevBrand) {
            if (prevBrand !== '') { h += '</optgroup>'; }
            h += '<optgroup label="' + escHtml(brand) + '">';
            prevBrand = brand;
          }
          h += '<option value="' + escHtml(d.handle) + '"' + (d.label === cfg.dev ? ' selected' : '') + '>' + escHtml(d.label) + '</option>';
        }
        if (prevBrand !== '') { h += '</optgroup>'; }
        h += '</select>';
      }
      h += '<button type="button" class="promo22__cfg-add" data-p11-pick-add' +
        (cfg.vid ? ' data-variant-id="' + cfg.vid + '"' : ' disabled') +
        ' data-p11-src="' + escHtml(cfg.src) + '" data-p11-name="' + escHtml(cfg.name) + '">' +
        (cfg.busy ? '…' : (cfg.vid ? 'Hozzáadom — ajándék tok' : 'Erre a telefonra nem elérhető')) +
        '</button>';
      box.innerHTML = h;
      box.hidden = false;
    }
    /* Full-Shock Black > Full-Shock any > first available - the same chain the
       server walks (snippets/promo22-bar.liquid) */
    function pickVariant(product) {
      var vs = (product && product.variants) || [];
      var pref = null, fsAny = null, anyAvail = null, i, v;
      for (i = 0; i < vs.length; i++) {
        v = vs[i];
        if (!v.available) { continue; }
        if (!anyAvail) { anyAvail = v; }
        if (v.option1 === 'Full-Shock') {
          if (!fsAny) { fsAny = v; }
          if (v.option2 === 'Black' && !pref) { pref = v; }
        }
      }
      return pref || fsAny || anyAvail || null;
    }
    function applyPick(pick) {
      if (!cfg) { return; }
      cfg.busy = false;
      cfg.vid = pick.vid;
      cfg.price = pick.price;
      renderCfg();
    }
    function resolveDevice(handle) {
      if (!cfg) { return; }
      if (cfg.cache[handle]) { applyPick(cfg.cache[handle]); return; }
      cfg.busy = true;
      cfg.vid = 0;
      cfg.price = '';
      renderCfg();
      fetch('/products/' + handle + '.js')
        .then(function (r) {
          if (!r.ok) { throw new Error('product ' + r.status); }
          return r.json();
        })
        .then(function (p) {
          var v = pickVariant(p);
          var pick = v ? { vid: v.id, price: fmtMoney(v.price) } : { vid: 0, price: '' };
          cfg.cache[handle] = pick;
          applyPick(pick);
        })
        .catch(function () { applyPick({ vid: 0, price: '' }); });
    }
    function openCfg(btn) {
      var devs = parseDevices(btn.getAttribute('data-p11-devices'));
      var dev = btn.getAttribute('data-p11-dev') || '';
      var vid = parseInt(btn.getAttribute('data-p11-default'), 10) || 0;
      var price = btn.getAttribute('data-p11-price') || '';
      cfg = {
        name: btn.getAttribute('data-p11-name') || '',
        src: btn.getAttribute('data-p11-src') || '',
        img: btn.getAttribute('data-p11-img') || '',
        vid: vid,
        price: price,
        dev: dev,
        devices: devs,
        busy: false,
        cache: {}
      };
      /* seed the cache so flipping back to the cart's device costs no fetch */
      for (var i = 0; i < devs.length; i++) {
        if (devs[i].label === dev) { cfg.cache[devs[i].handle] = { vid: vid, price: price }; break; }
      }
      track('pick_open', { mf_option_value: cfg.name });
      if (cfg.src === 'new') { track('pick_open_new'); }
      renderCfg();
    }
    D.documentElement.addEventListener('change', function (ev) {
      var sel = ev.target && ev.target.closest && ev.target.closest('[data-p11-dev-sel]');
      if (!sel || !cfg) { return; }
      var opt = sel.options[sel.selectedIndex];
      cfg.dev = opt ? (opt.textContent || '') : '';
      track('pick_dev_change');
      resolveDevice(sel.value);
    }, true);

    /* desktop drag-to-scroll for the tile strip; touch scrolls natively. The
       dragMoved flag suppresses the click that follows a drag, so a drag never
       opens the configurator by accident. */
    var dragMoved = false;
    D.documentElement.addEventListener('pointerdown', function (ev) {
      if (ev.pointerType === 'touch') { return; }
      var strip = ev.target && ev.target.closest && ev.target.closest('[data-p11-pick]');
      if (!strip) { return; }
      var sx = ev.clientX;
      var sl = strip.scrollLeft;
      dragMoved = false;
      function onMove(e2) {
        var dx = e2.clientX - sx;
        if (dx > 5 || dx < -5) { dragMoved = true; }
        strip.scrollLeft = sl - dx;
        e2.preventDefault();
      }
      function onUp() {
        W.removeEventListener('pointermove', onMove, true);
        W.removeEventListener('pointerup', onUp, true);
        setTimeout(function () { dragMoved = false; }, 0);
      }
      W.addEventListener('pointermove', onMove, true);
      W.addEventListener('pointerup', onUp, true);
    }, true);

    /* one delegated click listener serves both surfaces; a[data-p22] only
       exists once decoration ran, so card_click self-gates */
    D.documentElement.addEventListener('click', function (ev) {
      if (!ev.isTrusted) { return; }
      var po = ev.target && ev.target.closest && ev.target.closest('[data-p11-pick-open]');
      if (po) {
        if (!dragMoved) { openCfg(po); }
        return;
      }
      var cx = ev.target && ev.target.closest && ev.target.closest('[data-p11-cfg-close]');
      if (cx) {
        var bx = cfgBox();
        if (bx) { bx.hidden = true; bx.innerHTML = ''; }
        cfg = null;
        return;
      }
      var pk = ev.target && ev.target.closest && ev.target.closest('[data-p11-pick-add]');
      if (pk) { pickAdd(pk); return; }
      var a = ev.target && ev.target.closest && ev.target.closest('a[data-p22]');
      if (a) { track('card_click'); }
      /* the close button is dismissal, not interest - it must not count */
      if (ev.target && ev.target.closest && ev.target.closest('.glossy-announcement__close')) { return; }
      var bar = ev.target && ev.target.closest && ev.target.closest('.announcement-bar, [class*="announcement"]');
      if (bar && BAR_RE.test(bar.textContent || '')) { track('bar_click'); }
      /* manual drawer opens fire no cart:refresh - a deferred read catches the
         picker exposure (and any state edge) on those paths too */
      setTimeout(readDrawer, 700);
    }, true);

    /* the FIRST announcement-ish element in document order is Focal's empty
       section wrapper (its textContent is CSS) - scan them ALL and skip
       dismissed bars, or bar_view never fires while bar_click does */
    var anns = D.querySelectorAll('.announcement-bar, [class*="announcement"]');
    for (var ai = 0; ai < anns.length; ai++) {
      if (anns[ai].classList.contains('is-hidden')) { continue; }
      if (BAR_RE.test(anns[ai].textContent || '')) { trackOnce('bar_view'); break; }
    }

    /* PDP promo line */
    if (D.querySelector('[data-promo22-pdp]')) { trackOnce('pdp_view'); }

    /* drawer progress states + cart depth: read after every server re-render.
       The glossy add path re-renders the drawer only after an awaited
       /cart?section_id=mini-cart round-trip, so 600ms can be too early on slow
       connections - a second read catches the late swap. */
    var lastState = '';
    function readDrawer() {
      var el = D.querySelector('[data-promo22-state]');
      if (!el) { return; }
      var stt = el.getAttribute('data-promo22-state') || '';
      if (stt && stt !== lastState) {
        lastState = stt;
        track('drawer_' + stt);
      }
      /* picker exposure: the drawer exists CSS-hidden on every page, so
         presence alone would count pageviews - only a visible picker counts
         (the custom.js fol:resc_shown gate, custom.js:1443-1446) */
      var pick = D.querySelector('[data-p11-pick]');
      if (pick && pick.offsetWidth > 0 && getComputedStyle(pick).visibility !== 'hidden') {
        trackOnce('pick_view');
      }
      reportCases();
    }
    readDrawer();
    D.documentElement.addEventListener('cart:refresh', function () {
      setTimeout(readDrawer, 600);
      setTimeout(readDrawer, 1800);
    }, false);

    /* ── badge layer: the only part that needs the collection's handles ── */
    getHandles(collection).then(function (handles) {
      if (!handles.length) { return; }

      decorate(handles, badgeText);
      /* load-more and drawer re-renders inject new cards; a light debounce keeps
         the observer from thrashing during the drawer's innerHTML swaps */
      var t = null;
      new MutationObserver(function () {
        if (t) { clearTimeout(t); }
        t = setTimeout(function () { decorate(handles, badgeText); }, 400);
      }).observe(D.body, { childList: true, subtree: true });
    });
  }

  /* Defer scripts evaluate at readyState 'interactive', BEFORE DOMContentLoaded
     fires - and this file loads ahead of mf-track.js in the defer queue. Booting
     synchronously here would run the warm-cache (sessionStorage) path in a
     microtask while window.mfTrackPush does not exist yet, silently dropping
     every step fired at boot. DOMContentLoaded fires only after ALL defer
     scripts have executed, so waiting for it guarantees the tracker is up. */
  if (D.readyState === 'complete') { boot(); }
  else { D.addEventListener('DOMContentLoaded', boot, false); }
})();
